import Cookies from 'js-cookie'

import LogTool from '../../../logger/logTools'
import { Configuration, RequiredError, type InitOverrideFunction } from '../../../generated-types/runtime'
import { GuestRequestpartApi } from '../../../generated-types/apis/GuestRequestpartApi'
import { ItemsarticlesApi } from '../../../generated-types/apis/ItemsarticlesApi'
import { getPKfromSelf } from '../../../utils/functions'
import { createArticle } from '../../Item/api/articleApi'
import { type Article } from '../../Item/utils/types'
import { type BOMLeafArticle } from '../../Bom/api/bomApi'
import { APIPart, APIPartFromJSON, APIPartRequest, APIPartUpdate, APIPartUpdateFromJSON, APIPartUpdateRequest, APIPartUpdateRequestToJSON, V1RequestRequestsPartsRecalculateCreateRequest } from '../../../generated-types'
import { v1Api } from '../../../api/generatedClient'
import { apiClient } from '../../../api'

const log = new LogTool({ context: 'RequestPartAPI', enable: true, logLevel: 'warn' })

export interface FetchRequestPartInput {
  requestPk: string | number
  partId: string | number
  expand?: string[]
  accessSecret?: string
}

export interface FetchBomLeafArticlesInput {
  articleSelf: string
  accessSecret?: string
}

export interface RecalculateRequestPartInput {
  requestPk: string | number
  partId: string | number
  data: APIPartRequest
  accessSecret?: string
}

export interface UpdateRequestPartInput {
  requestPk: string | number
  partId: string | number
  data: APIPartUpdateRequest | FormData
  accessSecret?: string
}

function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }

  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => Cookies.get('access') || '',
  })
}

function buildInitOverrides(accessSecret?: string): InitOverrideFunction | undefined {
  if (!accessSecret) return undefined
  return ({ init }) =>
    Promise.resolve({
      ...init,
      headers: {
        ...(init.headers ?? {}),
        'X-ACCESS-SECRET': accessSecret,
      },
    })
}

class GuestRequestpartApiWithExpand extends GuestRequestpartApi {
  async v1RequestRequestsPartsRetrieveRawWithExpand(
    requestParameters: { id: string; requestPk: string; expand?: string },
    initOverrides?: RequestInit | InitOverrideFunction
  ): Promise<Response> {
    if (requestParameters['id'] == null) {
      throw new RequiredError(
        'id',
        'Required parameter "id" was null or undefined when calling v1RequestRequestsPartsRetrieve().'
      )
    }
    if (requestParameters['requestPk'] == null) {
      throw new RequiredError(
        'requestPk',
        'Required parameter "requestPk" was null or undefined when calling v1RequestRequestsPartsRetrieve().'
      )
    }

    const queryParameters: Record<string, string> = {}
    if (requestParameters.expand) {
      queryParameters['expand'] = requestParameters.expand
    }

    const headerParameters: Record<string, string> = {}

    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken
      const tokenString = await token('jwtAuth', [])
      if (tokenString) {
        headerParameters['Authorization'] = `Bearer ${tokenString}`
      }
    }

    return this.request(
      {
        path: `/v1/request/requests/{request_pk}/parts/{id}/`
          .replace(`{${'request_pk'}}`, encodeURIComponent(String(requestParameters['requestPk'])))
          .replace(`{${'id'}}`, encodeURIComponent(String(requestParameters['id']))),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides
    )
  }
}

function getPartsApi(): GuestRequestpartApiWithExpand {
  return new GuestRequestpartApiWithExpand(getApiConfiguration())
}

class ItemsarticlesApiWithExpand extends ItemsarticlesApi {
  async v1ItemsArticlesBomLeafArticlesRetrieveRawWithExpand(
    requestParameters: { id: number; expand?: string },
    initOverrides?: RequestInit | InitOverrideFunction
  ): Promise<Response> {
    if (requestParameters['id'] == null) {
      throw new RequiredError(
        'id',
        'Required parameter "id" was null or undefined when calling v1ItemsArticlesBomLeafArticlesRetrieve().'
      )
    }

    const queryParameters: Record<string, string> = {}
    if (requestParameters.expand) {
      queryParameters['expand'] = requestParameters.expand
    }

    const headerParameters: Record<string, string> = {}

    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken
      const tokenString = await token('jwtAuth', [])
      if (tokenString) {
        headerParameters['Authorization'] = `Bearer ${tokenString}`
      }
    }

    return this.request(
      {
        path: `/v1/items/articles/{id}/bom_leaf_articles/`.replace(
          `{${'id'}}`,
          encodeURIComponent(String(requestParameters['id']))
        ),
        method: 'GET',
        headers: headerParameters,
        query: queryParameters,
      },
      initOverrides
    )
  }
}

function getArticlesApi(): ItemsarticlesApiWithExpand {
  return new ItemsarticlesApiWithExpand(getApiConfiguration())
}

export async function fetchRequestPart({
  requestPk,
  partId,
  accessSecret,
}: FetchRequestPartInput): Promise<Article> {
  log.info('Fetching request part:', { requestPk, partId })

  const initOverrides = buildInitOverrides(accessSecret)
  const response = await getPartsApi().v1RequestRequestsPartsRetrieveRawWithExpand(
    {
      requestPk: String(requestPk),
      id: String(partId),
      expand: 'files,requests',
    },
    initOverrides
  )

  const payload = await response.json()
  return createArticle(payload)
}

export async function fetchBomLeafArticles({
  articleSelf,
  accessSecret,
}: FetchBomLeafArticlesInput): Promise<BOMLeafArticle[]> {
  log.info('Fetching BOM leaf articles for:', articleSelf)

  const articlePk = getPKfromSelf(articleSelf)
  const articleId = Number(articlePk)
  if (!Number.isFinite(articleId)) {
    throw new Error(`Invalid article id for BOM leaf articles: ${articleSelf}`)
  }
  const initOverrides = buildInitOverrides(accessSecret)
  const response = await getArticlesApi().v1ItemsArticlesBomLeafArticlesRetrieveRawWithExpand(
    { id: articleId, expand: 'files,requests,requests__article' },
    initOverrides
  )

  const payload = await response.json()
  if (Array.isArray(payload)) {
    return payload as BOMLeafArticle[]
  }
  if (payload && Array.isArray(payload.results)) {
    return payload.results as BOMLeafArticle[]
  }
  return []
}

export async function recalculateRequestPartApi({
  requestPk,
  partId,
  data,
  accessSecret,
}: RecalculateRequestPartInput): Promise<APIPart> {
  log.info('Recalculating guest request part:', { requestPk, partId })

  const requestParameters: V1RequestRequestsPartsRecalculateCreateRequest = {
    requestPk: String(requestPk),
    id: String(partId),
    aPIPartRequest: data,
  }

  const initOverrides: InitOverrideFunction | undefined = accessSecret
    ? ({ init }) =>
        Promise.resolve({
          ...init,
          headers: {
            ...(init.headers ?? {}),
            'X-ACCESS-SECRET': accessSecret,
          },
        })
    : undefined

  return v1Api.v1RequestRequestsPartsRecalculateCreate(requestParameters, initOverrides)
}

export async function updateRequestPartApi({
  requestPk,
  partId,
  data,
  accessSecret,
}: UpdateRequestPartInput): Promise<APIPartUpdate> {
  log.info('Updating guest request part:', { requestPk, partId })

  const url = `v1/request/requests/${requestPk}/parts/${partId}/`
  const isFormData = data instanceof FormData
  const headers: Record<string, string> = {}
  if (accessSecret) {
    headers['X-ACCESS-SECRET'] = accessSecret
  }

  if (!isFormData) {
    headers['Content-Type'] = 'application/json'
  }

  const payload = isFormData ? data : APIPartUpdateRequestToJSON(data)
  if (!isFormData) {
    log.debug('Converted APIPartUpdateRequest to JSON:', payload)
  }
  const response = await apiClient.put<any>(url, payload, headers, isFormData)
  return APIPartUpdateFromJSON(response)
}

export async function fetchRequestPartApi({
  requestPk,
  partId,
  expand,
  accessSecret,
}: FetchRequestPartInput): Promise<APIPart> {
  log.info('Fetching guest request part:', { requestPk, partId, expand })

  const url = `v1/request/requests/${requestPk}/parts/${partId}/`
  const params = expand && expand.length > 0 ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const headers: Record<string, string> = {}
  if (accessSecret) {
    headers['X-ACCESS-SECRET'] = accessSecret
  }

  const response = await apiClient.get<any>(fetchUrl, headers)
  return APIPartFromJSON(response)
}

